import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../../assets/styles/card.css';
import {
  FaBoxOpen,
  FaSyncAlt,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryQuarter,
  FaBatteryEmpty,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import BASE_URL from '../../config';

function CaseInfoCard() {
  const [caseData, setCaseData] = useState(null);
  const [caseStatus, setCaseStatus] = useState('INACTIVE');
  const [caseStatusDetails, setCaseStatusDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCaseDetails = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('jwt_token');

    if (!token) {
      console.warn('No authentication token found');
      setLoading(false);
      setError('No authentication token found');
      return;
    }

    try {
      // First, get the device UID
      const deviceResponse = await axios.post(
        `${BASE_URL}/api/get_cust_devices/`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const deviceData = Array.isArray(deviceResponse.data)
        ? deviceResponse.data[0]
        : deviceResponse.data;

      if (!deviceData || !deviceData.uid) {
        console.warn('No valid device data found.');
        setLoading(false);
        setError('No device found');
        return;
      }

      // Fetch case details using the device UID
      const caseResponse = await axios.get(
        `${BASE_URL}/api/pocket/get-case-details/${deviceData.uid}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCaseData(caseResponse.data);

      // Fetch case status details using the device UID
      try {
        const caseStatusResponse = await axios.get(
          `${BASE_URL}/api/pocket/get-case-status/${deviceData.uid}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCaseStatusDetails(caseStatusResponse.data);
      } catch (statusError) {
        console.warn('No case status data found:', statusError);
      }

      // Calculate case activity status based on last seen time
      if (caseResponse.data.latest_status?.timestamp) {
        const lastSeenTime = new Date(caseResponse.data.latest_status.timestamp);
        const currentTime = new Date();
        const diffInMinutes = (currentTime - lastSeenTime) / (1000 * 60);
        setCaseStatus(diffInMinutes <= 10 ? 'ACTIVE' : 'INACTIVE');
      } else {
        setCaseStatus('UNKNOWN');
      }
    } catch (error) {
      console.error('Error fetching case details:', error);
      setError(error.response?.data?.error || 'Failed to fetch case details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseDetails();
  }, []);

  const getBatteryIcon = (battery) => {
    if (battery >= 75) return <FaBatteryFull aria-hidden="true" />;
    if (battery >= 50) return <FaBatteryHalf aria-hidden="true" />;
    if (battery >= 25) return <FaBatteryQuarter aria-hidden="true" />;
    return <FaBatteryEmpty aria-hidden="true" />;
  };

  const getBatteryColor = (battery) => {
    if (battery >= 75) return '#4caf50';
    if (battery >= 50) return '#ffeb3b';
    if (battery >= 25) return '#ff9800';
    return '#f44336';
  };

  const getStatusDotClass = (status) => {
    if (status === 'ACTIVE') {
      return 'device-card-dot device-card-dot-green';
    } else if (!status || status === 'UNKNOWN') {
      return 'device-card-dot device-card-dot-red';
    } else {
      return 'device-card-dot device-card-dot-gold';
    }
  };

  return (
    <article className="device-card-wrapper" aria-labelledby="case-info-title">
      <div className="device-card-title-bar">
        <h2 id="case-info-title" className="device-card-title">Case Information</h2>
        <button className="refresh-button" onClick={fetchCaseDetails} aria-label="Refresh case information">
          <FaSyncAlt aria-hidden="true" /> Refresh
        </button>
      </div>

      {loading ? (
        <p role="status" aria-live="polite">Loading...</p>
      ) : error ? (
        <p className="error-message" role="alert">{error}</p>
      ) : !caseData ? (
        <p role="status">No case found.</p>
      ) : (
        <div className="device-card-grid">
          <div className="device-card-container">
            <div className="device-card-header">
              <div className="device-card-id">
                <FaBoxOpen aria-hidden="true" />
                <span className="uid-text">{caseData.serial}</span>
              </div>
              <div className="device-card-status">
                <span
                  className={getStatusDotClass(caseStatus)}
                  role="img"
                  aria-label={`Case status: ${caseStatus}`}
                > </span>
                <span className="last-seen-label">Last Updated:</span>
                <span>
                  {caseStatusDetails?.timestamp
                    ? new Date(caseStatusDetails.timestamp).toLocaleString('en-GB')
                    : 'No data'}
                </span>
              </div>
            </div>

            <div id="case-info-body" className="device-card-body">
              {/* Battery Info */}
              {caseStatusDetails?.battery !== undefined ? (
                <div className="device-card-row battery-row">
                  <div className="battery-header">
                    <strong>Battery:</strong> {caseStatusDetails.battery}%{' '}
                    {getBatteryIcon(caseStatusDetails.battery)}
                  </div>
                  <div
                    className="battery-bar-container"
                    role="progressbar"
                    aria-valuenow={caseStatusDetails.battery}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Battery level: ${caseStatusDetails.battery}%`}
                  >
                    <div
                      className="battery-bar-fill"
                      style={{
                        width: `${caseStatusDetails.battery}%`,
                        backgroundColor: getBatteryColor(caseStatusDetails.battery),
                      }}
                    ></div>
                  </div>
                </div>
              ) : (
                <p>No battery data available</p>
              )}

              {/* Case Status Details -- rendered as a description list so
                  screen readers pair each label with its own value */}
              {caseStatusDetails && (
                <dl className="device-card-details">
                  <div className="device-card-row">
                    <dt className="device-card-term">Charging:</dt>
                    <dd className="device-card-value">
                      {caseStatusDetails.charging ? 'Yes' : 'No'}
                    </dd>
                  </div>
                  <div className="device-card-row">
                    <dt className="device-card-term">Glasses inside:</dt>
                    <dd className="device-card-value">
                      {caseStatusDetails.glasses_inside ??
                        (caseStatusDetails.glasses_detected ? 'Yes' : 'No')}
                    </dd>
                  </div>
                  <div className="device-card-row">
                    <dt className="device-card-term">LTE Signal Level:</dt>
                    <dd className="device-card-value">
                      {caseStatusDetails.lte_signal_level}
                    </dd>
                  </div>
                  <div className="device-card-row">
                    <dt className="device-card-term">Station Count:</dt>
                    <dd className="device-card-value">
                      {caseStatusDetails.station_count}
                    </dd>
                  </div>
                  <div className="device-card-row">
                    <dt className="device-card-term">Operator:</dt>
                    <dd className="device-card-value">
                      {caseStatusDetails.operator || '—'}
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default CaseInfoCard;
